<template lang="pug">
    //- Articles that carry their own copy of product data win in the shop. Say so where the
    //- product data is edited (api docs/features/PRODUCT_ARTICLE_INHERITANCE.md section 10).
    .alert.warning.article-overrides-notice(v-if="lines.length" role="note")
        template(v-if="isSingleArticleProduct")
            p {{ $t('The article still has its own {kinds}. Until the next save the shop shows them instead of the values here, saving the product replaces them.', { kinds: kindList }) }}
        template(v-else)
            p(v-for="line in lines" :key="line.kind") {{ $tc(line.message, line.count, { count: line.count, total }) }}
            p {{ $t('For these articles the shop shows their own values.') }}
                |
                |
                router-link(:to="`/catalog/product/${form.id}/articles`") {{ $t('Go to the articles') }}
</template>

<i18n>
{
    "en": {
        "{count} of {total} articles have their own prices": "{count} of {total} articles has its own prices. | {count} of {total} articles have their own prices.",
        "{count} of {total} articles have their own name": "{count} of {total} articles has its own name. | {count} of {total} articles have their own name.",
        "{count} of {total} articles have their own texts": "{count} of {total} articles has its own texts. | {count} of {total} articles have their own texts.",
        "{count} of {total} articles have their own media": "{count} of {total} articles has its own media. | {count} of {total} articles have their own media.",
        "{count} of {total} articles have their own tags": "{count} of {total} articles has its own tags. | {count} of {total} articles have their own tags.",
        "{count} of {total} articles have further values of their own": "{count} of {total} articles has further values of its own. | {count} of {total} articles have further values of their own."
    },
    "de": {
        "The article still has its own {kinds}. Until the next save the shop shows them instead of the values here, saving the product replaces them.": "Der Artikel hat noch eigene {kinds}. Bis zum nächsten Speichern zeigt der Shop diese statt der Angaben hier, das Speichern des Produkts ersetzt sie.",
        "{count} of {total} articles have their own prices": "{count} von {total} Artikeln hat eigene Preise. | {count} von {total} Artikeln haben eigene Preise.",
        "{count} of {total} articles have their own name": "{count} von {total} Artikeln hat einen eigenen Namen. | {count} von {total} Artikeln haben einen eigenen Namen.",
        "{count} of {total} articles have their own texts": "{count} von {total} Artikeln hat eigene Texte. | {count} von {total} Artikeln haben eigene Texte.",
        "{count} of {total} articles have their own media": "{count} von {total} Artikeln hat eigene Medien. | {count} von {total} Artikeln haben eigene Medien.",
        "{count} of {total} articles have their own tags": "{count} von {total} Artikeln hat eigene Tags. | {count} von {total} Artikeln haben eigene Tags.",
        "{count} of {total} articles have further values of their own": "{count} von {total} Artikeln hat weitere eigene Angaben. | {count} von {total} Artikeln haben weitere eigene Angaben.",
        "For these articles the shop shows their own values.": "Im Shop gelten für diese Artikel deren eigene Angaben.",
        "Go to the articles": "Zu den Artikeln",
        "prices": "Preise",
        "name": "Namen",
        "texts": "Texte",
        "media": "Medien",
        "tags": "Tags",
        "further values": "weiteren Angaben",
        "and": "und"
    }
}
</i18n>

<script>
import _ from 'lodash';
import pinia from '@/pinia';
import { useShopProductStore } from '@/stores';

// One message per kind of override, in the order the counts come from the API
const KINDS = [
    { kind: 'prices', message: '{count} of {total} articles have their own prices', noun: 'prices' },
    { kind: 'name', message: '{count} of {total} articles have their own name', noun: 'name' },
    { kind: 'description', message: '{count} of {total} articles have their own texts', noun: 'texts' },
    { kind: 'media', message: '{count} of {total} articles have their own media', noun: 'media' },
    { kind: 'tags', message: '{count} of {total} articles have their own tags', noun: 'tags' },
    { kind: 'other', message: '{count} of {total} articles have further values of their own', noun: 'further values' },
];

export default {
    name: 'ArticleOverridesNotice',
    props: {
        form: {
            type: Object,
            required: true,
        },
        // which kinds of overrides this screen edits, e.g. ['prices'] on the prices tab
        kinds: {
            type: Array,
            required: true,
        },
    },
    computed: {
        total() {
            return _.get(this.form, 'articleOverrides.articles') || 0;
        },
        isSingleArticleProduct() {
            return useShopProductStore(pinia).isSingleArticleProduct;
        },
        lines() {
            return KINDS.filter((entry) => this.kinds.includes(entry.kind))
                .map((entry) => ({ ...entry, count: _.get(this.form, ['articleOverrides', entry.kind]) || 0 }))
                .filter((entry) => entry.count > 0);
        },
        kindList() {
            const nouns = this.lines.map((line) => this.$t(line.noun));
            if (nouns.length < 2) return nouns.join('');

            return `${nouns.slice(0, -1).join(', ')} ${this.$t('and')} ${nouns[nouns.length - 1]}`;
        },
    },
};
</script>

<style lang="sass" scoped>
.article-overrides-notice
    // owns its spacing: the global .alert adds 20px above, which opens a gap at the top of a tab or drawer
    margin: 0 0 24px

    p
        margin: 0
        & + p
            margin-top: .25rem
</style>
